/**
 * Facility-wide lookup of which patients currently have an open collaboration
 * note, used to light up the notification worklist's notes action.
 *
 * The notification list itself does not carry note state (rows are built with
 * `notes: []`), so this hook cross-references the facility notes inbox once and
 * exposes the set of MRNs with at least one open note.
 */

import { useEffect, useState } from "react";
import { clinicalTrialsService } from "../api/clinicalTrialsService";

/** How many notes to request per inbox page while collecting open-note MRNs. */
const NOTES_PAGE_SIZE = 100;

/**
 * Hard cap on inbox pages walked in one pass. At {@link NOTES_PAGE_SIZE} per
 * page this covers a very large open-notes inbox while guaranteeing the loop
 * can never become an unbounded request storm if the server misbehaves.
 */
const MAX_NOTES_PAGES = 50;

/**
 * Load the set of MRNs that have at least one open note for the facility.
 *
 * Pages through the cursor-paginated open-notes inbox and collects every MRN.
 * The loop is self-limiting: it stops on a repeated cursor (no forward
 * progress) or once {@link MAX_NOTES_PAGES} is reached, so a bad server
 * response can never turn a mount into an infinite request loop. Best-effort:
 * on any failure the returned set is left empty so the worklist simply falls
 * back to the default (no notes) rendering.
 *
 * @param refreshKey - Optional value; change it to force a re-pull (e.g. after
 *   a note is created or resolved while the worklist stays mounted).
 * @returns A set of MRNs with at least one open note.
 */
export function useOpenNoteMrns(refreshKey?: number): Set<string> {
  const [mrns, setMrns] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    /**
     * Walk the pages of the open-notes inbox, accumulating MRNs, with guards
     * against a non-progressing cursor and an excessive page count.
     *
     * @returns Promise resolved after the inbox has been paged through.
     */
    const loadAllOpenNoteMrns = async (): Promise<void> => {
      const collected = new Set<string>();
      const seenCursors = new Set<string>();
      let cursor: string | undefined;
      let pages = 0;
      try {
        do {
          const page = await clinicalTrialsService.getNotes(
            { status: "open", page_size: NOTES_PAGE_SIZE, cursor },
            controller.signal,
          );
          page.hits.forEach((note) => {
            if (note.mrn) collected.add(note.mrn);
          });
          const next = page.has_more ? (page.next_cursor ?? undefined) : undefined;
          // Advance only to a cursor we have not already followed; otherwise stop.
          cursor = next && !seenCursors.has(next) ? next : undefined;
          if (cursor) seenCursors.add(cursor);
          pages += 1;
        } while (cursor && pages < MAX_NOTES_PAGES);
        if (!cancelled) setMrns(collected);
      } catch {
        // Best-effort: leave the worklist notes indicator in its default state.
      }
    };

    void loadAllOpenNoteMrns();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [refreshKey]);

  return mrns;
}
